import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tag } from 'antd';
import shallow from 'zustand/shallow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import { getContainerById } from '../../store/lookups';
import BoxFormControlsPortal from './BoxFormControlsPortal';
import { PRIMARY_DARK } from '../../constants/constants';
import { sectionEditorUrl } from '../../utils/browser';

// Origine du contenu sélectionné, en mode MODÈLE uniquement.
//
// Une section posée dans un modèle l'est de deux façons (cf. le toggle de
// SelectionBox, `linked_section`) :
//  - LIÉE   : `from_linked_section` posé sur containers/colonnes/box à
//             l'injection (utils/sections.js). Le contenu appartient à la
//             section source, il est réinjecté à chaque chargement du modèle :
//             toute édition sur place serait écrasée, elle est donc interdite.
//  - COPIÉE : contenu détaché, éditable, figé à la date d'injection.
//
// Les deux se ressemblent trait pour trait sur le canvas. Sans cette section de
// panel, un container lié n'offre que des contrôles absents (édition bloquée
// par useHandleBoxSelect, disposition retirée du panel) sans jamais dire
// pourquoi, et une copie laisse croire que modifier la section source suffira.
export const selector = ({ currentDocument, selectedBoxId, boxes, containers, activeContainerId, sections }) => {
  if (currentDocument?.type !== 'template') return null;

  // Priorité à la box sélectionnée : dans une section COPIÉE les box restent
  // sélectionnables, et c'est elle qui porte l'origine la plus précise
  // (`fromSectionId` = section d'où vient réellement ce contenu, y compris pour
  // une sous-section imbriquée). Une section LIÉE bloque la sélection de ses
  // box (hooks.js), et une copie ancienne n'a pas de `fromSectionId` sur ses
  // box : on retombe alors sur le container actif.
  const selectedBox = selectedBoxId ? boxes.find((b) => b.id === selectedBoxId) : null;
  const container = getContainerById(containers, activeContainerId);
  // La box sélectionnée peut avoir disparu (suppression de son container) : sans
  // elle ni container actif, il n'y a pas d'origine à afficher.
  const source = selectedBox?.fromSectionId != null ? selectedBox : container;
  if (!source) return null;
  const sectionId = source === selectedBox ? selectedBox.fromSectionId : source.sectionId;
  if (sectionId == null) return null;

  return {
    sectionId,
    isLinked: !!source.from_linked_section,
    dateTimestamp: source.dateTimestamp ?? null,
    sectionName: sections.find((s) => String(s.id) === String(sectionId))?.name ?? null,
  };
};

// Ouvre la section source dans un nouvel onglet, comme l'arbre des sections et
// la liste des sections liées du panel gauche (?tid=).
const openSectionInTab = (sectionId) => {
  window.open(sectionEditorUrl(sectionId), '_blank', 'noopener,noreferrer');
};

// Toujours la dernière section du panel. C'est une information de contexte, pas
// un réglage : la placer en tête décalait vers le bas les Actions, l'Affichage
// et le Texte selon que la box vienne d'une section ou non — les boutons
// changeaient de place d'une sélection à l'autre. Au-dessus de tous les `order`
// utilisés par les autres sections (max 3 aujourd'hui, cf. SelectionBox).
const SECTION_ORIGIN_ORDER = 99;

const SectionOriginPanel = () => {
  const { t, i18n } = useTranslation();
  // shallow : le sélecteur reconstruit un objet à chaque appel, sinon le panel
  // se re-rendrait à la moindre frappe dans le document.
  const origin = useStore(selector, shallow);
  if (!origin) return null;

  const { sectionId, sectionName, isLinked, dateTimestamp } = origin;
  const injectedAt = dateTimestamp
    ? new Date(dateTimestamp).toLocaleDateString(i18n.language)
    : null;

  return (
    <BoxFormControlsPortal
      title={t('section_origin_title')}
      layout='column'
      order={SECTION_ORIGIN_ORDER}
      // Renseigne la section d'origine du container, pas la box : reste visible
      // même quand plusieurs box sont sélectionnées.
      keepOnMultiSelection
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Tag color={isLinked ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
          {isLinked ? t('section_origin_linked') : t('section_origin_copied')}
        </Tag>
        {sectionName && (
          <span style={{ color: PRIMARY_DARK, fontSize: 12, fontWeight: 500 }}>{sectionName}</span>
        )}
      </div>

      <div style={{ fontSize: 11, lineHeight: 1.4, color: 'rgba(11, 34, 57, 0.65)' }}>
        {isLinked
          ? t('section_origin_linked_hint')
          : injectedAt
            ? t('section_origin_copied_hint_dated', { date: injectedAt })
            : t('section_origin_copied_hint')}
      </div>

      <Button
        size='small'
        icon={<FontAwesomeIcon icon={icon({ name: 'arrow-up-right-from-square', style: 'light' })} />}
        onClick={() => openSectionInTab(sectionId)}
      >
        {t('section_origin_open')}
      </Button>
    </BoxFormControlsPortal>
  );
};

export default SectionOriginPanel;
